// Notemaker editor: title + rich-text body (contentEditable) with a formatting
// toolbar, checklists, markdown-style shortcuts, colour, tags and the note's
// actions. The parent owns persistence — this component only reports edits
// via onChange (debounced upstream) and metadata flips via onMeta.
//
// Mount it with key={note.id}: the body is an uncontrolled contentEditable
// whose innerHTML is set once on mount, so a different note must remount.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ArrowDownAZ,
  ArrowLeft,
  ArrowUpAZ,
  Baseline,
  Bold,
  CalendarPlus,
  Check,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Copy,
  Download,
  Filter,
  FilterX,
  History,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Loader2,
  Minus,
  MoreHorizontal,
  PaintBucket,
  Palette,
  Pin,
  PinOff,
  Printer,
  Redo2,
  RemoveFormatting,
  RotateCcw,
  Strikethrough,
  Table2,
  Trash2,
  Underline,
  Undo2,
  Users,
  X,
} from "lucide-react";
import { notesApi } from "../../api/notes";
import type { Note, NoteColor, NoteEdit as NoteEditEntry, NotePatch } from "../../api/notes";
import {
  FORMULA_FUNCTIONS,
  FORMULA_MAX,
  INK_LABELS,
  NOTE_INK_KEYS,
  NOTE_SWATCHES,
  cellMatchesFilter,
  cellSortText,
  cellsToTsv,
  colName,
  countWords,
  deleteIndexMap,
  distinctColumnValues,
  evaluateTable,
  fullTimestamp,
  htmlToText,
  insertIndexMap,
  isBlankHtml,
  moveIndexMap,
  normalizeTag,
  orderIndexMap,
  parseTsv,
  relativeTime,
  rewriteFormulaRefs,
  sanitizeHtml,
  sortRowOrder,
  swatchFor,
} from "./noteUtils";
import type { FilterMode, NoteInkKey, RefRewrite, SortDir } from "./noteUtils";

export type SaveState = "idle" | "dirty" | "saving" | "saved" | "error";

export type NoteAction =
  | "duplicate"
  | "copy"
  | "download"
  | "print"
  | "planner"
  | "share"
  | "trash"
  | "restore"
  | "deleteForever";

interface NoteEditorProps {
  note: Note;
  saveState: SaveState;
  tagSuggestions: string[];
  onChange: (patch: { title?: string; content?: string }) => void;
  onMeta: (patch: NotePatch) => void;
  onAction: (action: NoteAction) => void;
  onSaveNow: () => void | Promise<unknown>;
  onBack: () => void;
  /** True for trashed notes: read-only body plus the Trash banner
   *  (restore / delete forever). Notes shared with me are editable. */
  readOnly?: boolean;
  /** Focus the title on mount (freshly created note). */
  autoFocusTitle?: boolean;
}

type BlockTag = "p" | "h1" | "h2" | "h3" | "blockquote" | "pre";

interface FormatState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  ul: boolean;
  ol: boolean;
  checklist: boolean;
  table: boolean;
  block: BlockTag;
  /** Palette token under the caret: text colour and, in a cell, its fill. */
  ink: NoteInkKey | null;
  fill: NoteInkKey | null;
}

const EMPTY_FORMAT: FormatState = {
  bold: false, italic: false, underline: false, strike: false, ul: false, ol: false, checklist: false, table: false, block: "p",
  ink: null, fill: null,
};

const TABLE_MAX = 20;

// ── Column widths ────────────────────────────────────────────────────
// A resized column stores a BUCKET on its header cell, never a width:
// `data-w="5"` is 5 × 5% = 25% of the table, and notemaker.css maps the 20
// buckets to a `width:` rule. See noteUtils.isColWidth — the encoding is
// documented there and checked by all four sanitizers.
/** Percent of the table's width one bucket is worth. */
const COL_W_STEP = 5;
const COL_W_MAX = 20;
/** Pointer this close to a header cell's right border resizes the column. */
const COL_RESIZE_ZONE = 12;
/** Pointer this close to a header cell's left edge drags the column. */
const COL_GRIP_ZONE = 18;
/** A press has to travel this far before it counts as a drag, not a click. */
const COL_DRAG_SLOP = 3;

const widthBucket = (pct: number): number =>
  Math.min(COL_W_MAX, Math.max(1, Math.round(pct / COL_W_STEP)));

/** The Rows/Columns boxes hold raw text while they are being typed, so
 *  backspacing one empty leaves it empty instead of snapping to 1 and making
 *  the next digit land after it ("1" + "5" = 15). The size is decided here, on
 *  Insert, and an empty box means 1. Mirrors the Messenger copy. */
const clampTableSize = (value: string): number =>
  Math.min(TABLE_MAX, Math.max(1, Math.floor(Number(value)) || 1));

/** Body rows of a table: every row after the header row (row 1). */
const bodyRows = (table: HTMLTableElement): HTMLTableRowElement[] => Array.from(table.rows).slice(1);

/** One column's body values, as sorting and filtering see them. */
const columnValues = (table: HTMLTableElement, col: number): string[] =>
  bodyRows(table).map((tr) => cellSortText(tr.cells[col]?.textContent || ""));

/** One table column: what the Table popover's sort/filter block acts on. */
interface ColumnTarget {
  table: HTMLTableElement;
  col: number;
}

/** What the Table popover needs to show about that column. */
interface ColumnState {
  col: number;
  /** The column's header text, or "Column C" when the header is blank. */
  label: string;
  values: string[];
  sort: SortDir | null;
  hidden: number;
  rows: number;
}

const columnStateFor = (target: ColumnTarget | null): ColumnState | null => {
  if (!target) return null;
  const { table, col } = target;
  const header = table.rows[0]?.cells[col];
  const rows = bodyRows(table);
  return {
    col,
    label: cellSortText(header?.textContent || "") || `Column ${colName(col)}`,
    values: columnValues(table, col),
    sort: (header?.getAttribute("data-sort") as SortDir | null) || null,
    hidden: rows.filter((tr) => tr.hasAttribute("data-filtered")).length,
    rows: rows.length,
  };
};

/** Excel address of a cell: column letter + 1-based row (header row = 1). */
const cellAddress = (cell: HTMLTableCellElement): string =>
  `${colName(cell.cellIndex)}${(cell.parentElement as HTMLTableRowElement).rowIndex + 1}`;

// ── Cell ranges ──────────────────────────────────────────────────────
// A rectangle of cells, as the mouse, Shift+click and Ctrl/Cmd+A pick it out
// and as copy, cut and paste then act on it. `anchor` is the cell the
// selection started from and `head` the one it reached — keeping both is what
// lets Shift+click extend a range — and the rectangle between them is what is
// selected.
//
// The selection is painted with a CLASS. `class` is in no sanitizer's
// allow-list, so a selection can never reach a saved note even by accident,
// and serializeBody strips it before the note is emitted anyway.
interface CellRange {
  table: HTMLTableElement;
  anchor: { row: number; col: number };
  head: { row: number; col: number };
}

const CELL_SEL = "nm-cell-sel";

/** The rectangle `range` covers, in row/column indices, lowest first. */
const rangeRect = (range: CellRange) => ({
  r0: Math.min(range.anchor.row, range.head.row),
  r1: Math.max(range.anchor.row, range.head.row),
  c0: Math.min(range.anchor.col, range.head.col),
  c1: Math.max(range.anchor.col, range.head.col),
});

/** The range's cells, row by row; a short row simply gives fewer cells. */
const rangeCells = (range: CellRange): HTMLTableCellElement[][] => {
  const { r0, r1, c0, c1 } = rangeRect(range);
  const out: HTMLTableCellElement[][] = [];
  for (let r = r0; r <= r1; r += 1) {
    const row = range.table.rows[r];
    if (!row) continue;
    const cells: HTMLTableCellElement[] = [];
    for (let c = c0; c <= c1; c += 1) if (row.cells[c]) cells.push(row.cells[c]);
    if (cells.length) out.push(cells);
  }
  return out;
};

/** Where a cell sits in its table (header row = row 0). */
const cellCoord = (cell: HTMLTableCellElement) => ({
  row: (cell.parentElement as HTMLTableRowElement).rowIndex,
  col: cell.cellIndex,
});

/** One cell of a grid on its way into a table. */
interface PasteCell {
  html: string;
  fill: string | null;
  color: string | null;
  /** The cell's formula, if the payload came from a Notemaker table. */
  formula: string | null;
}

// ── Where a copied grid came from ────────────────────────────────────
// A formula can only be carried across a paste if it is known WHICH CELL it
// was copied from, so the copy writes that cell on the clipboard table as
// data-nm-origin="row,col". It rides on the RAW clipboard HTML and is read
// before sanitizing: `table` has no allow-listed attribute in any of the four
// sanitizers, so the marker cannot reach a saved note — nothing new is
// persisted, and each sanitizer has a test pinning that it is dropped.
const ORIGIN_ATTR = "data-nm-origin";
const ORIGIN_RE = new RegExp(`<table[^>]*\\s${ORIGIN_ATTR}="(\\d{1,4}),(\\d{1,4})"`, "i");

/**
 * A payload's formulas as a TABLE OF THEIR OWN: a pasted table starts at A1,
 * so every ref moves by the delta from the cell it was copied from back to
 * that corner. With no origin to move them from, data-formula is dropped and
 * the cells keep the values they already show.
 */
const retargetFormulas = (payload: string, origin: { row: number; col: number } | null): string => {
  if (!/data-formula=/i.test(payload)) return payload;
  const holder = document.createElement("div");
  holder.innerHTML = payload;
  holder.querySelectorAll("[data-formula]").forEach((cell) => {
    const was = cell.getAttribute("data-formula") || "";
    if (!origin) cell.removeAttribute("data-formula");
    else cell.setAttribute("data-formula", rewriteFormulaRefs(was, { dc: -origin.col, dr: -origin.row }));
  });
  return holder.innerHTML;
};

/** The cell a clipboard grid's top-left corner was copied from, if it says. */
const gridOrigin = (rawHtml: string): { row: number; col: number } | null => {
  const m = ORIGIN_RE.exec(rawHtml);
  return m ? { col: parseInt(m[2], 10), row: parseInt(m[1], 10) } : null;
};

/**
 * The first table of a SANITIZED clipboard payload as a rectangular grid.
 * Anything else in the payload is dropped — a cell is filled with cell
 * contents — and the cells can never hold a table of their own, because the
 * sanitizer has already unwrapped a table nested in a td/th.
 */
const gridFromHtml = (payload: string): PasteCell[][] | null => {
  if (!/<table[\s>]/i.test(payload)) return null;
  const holder = document.createElement("div");
  holder.innerHTML = payload;
  const table = holder.querySelector("table");
  if (!table) return null;
  const rows = Array.from(table.querySelectorAll("tr")).map((tr) =>
    (Array.from(tr.children) as HTMLElement[]).map((cell) => ({
      color: cell.getAttribute("data-color"),
      fill: cell.getAttribute("data-fill"),
      formula: cell.getAttribute("data-formula"),
      html: cell.innerHTML || "<br>",
    })));
  const width = rows.reduce((w, row) => Math.max(w, row.length), 0);
  if (!width) return null;
  const blank: PasteCell = { color: null, fill: null, formula: null, html: "<br>" };
  return rows.map((row) => (row.length === width ? row : row.concat(Array(width - row.length).fill(blank))));
};

/** Tab/newline text as a grid — what a spreadsheet, a .tsv file or a terminal
 *  puts on the clipboard. Text with neither is one value, not a grid. */
const gridFromText = (text: string): PasteCell[][] | null => {
  if (!/[\t\r\n]/.test(text)) return null;
  const rows = parseTsv(text);
  if (!rows.length || !rows[0].length) return null;
  return rows.map((row) => row.map((value) => ({
    color: null,
    fill: null,
    formula: null,
    html: escapeHtml(value).replace(/\r?\n/g, "<br>") || "<br>",
  })));
};

/**
 * Move every ref in `table`'s formulas, because its rows or columns did.
 * The single door between the table tools and a formula's text: each tool
 * works out the map (or the delta) for what it just changed and this writes
 * the result back — see rewriteFormulaRefs for what the moves mean.
 */
const rewriteTableRefs = (table: HTMLTableElement, move: RefRewrite) => {
  table.querySelectorAll("[data-formula]").forEach((cell) => {
    const was = cell.getAttribute("data-formula") || "";
    const now = rewriteFormulaRefs(was, move);
    if (now !== was) cell.setAttribute("data-formula", now);
  });
};

/**
 * Re-run every formula cell in `root`'s tables and write the results into
 * the cells. `skip` is the cell being edited — it keeps showing its formula.
 */
const recalcTables = (root: HTMLElement, skip: HTMLElement | null) => {
  root.querySelectorAll("table").forEach((table) => {
    const cells = Array.from((table as HTMLTableElement).rows).map((row) => Array.from(row.cells));
    if (!cells.some((row) => row.some((c) => c.hasAttribute("data-formula")))) return;
    const out = evaluateTable(cells.map((row) => row.map((c) => ({
      formula: c.getAttribute("data-formula"),
      text: c.textContent || "",
    }))));
    cells.forEach((row, r) => row.forEach((c, i) => {
      const value = out[r][i];
      if (value === null || c === skip || c.textContent === value) return;
      if (value) c.textContent = value;
      else c.innerHTML = "<br>";
    }));
  });
};

/** Body HTML as saved: every formula cell shows its value, even the one
 *  being edited on screen, and no cell carries the range-selection class. */
const serializeBody = (el: HTMLElement): string => {
  if (!el.querySelector(`[data-formula],.${CELL_SEL}`)) return el.innerHTML;
  const snap = el.cloneNode(true) as HTMLElement;
  recalcTables(snap, null);
  snap.querySelectorAll(`.${CELL_SEL}`).forEach((cell) => {
    cell.classList.remove(CELL_SEL);
    if (!cell.getAttribute("class")) cell.removeAttribute("class");
  });
  return snap.innerHTML;
};

// A fresh grid: header row + (rows − 1) body rows, every cell holding a
// <br> so the caret can land in it.
const tableHtml = (rows: number, cols: number): string => {
  const cell = (tag: "th" | "td") => `<${tag}><br></${tag}>`;
  const row = (tag: "th" | "td") => `<tr>${Array(cols).fill(cell(tag)).join("")}</tr>`;
  const body = Array(Math.max(0, rows - 1)).fill(row("td")).join("");
  return `<table><thead>${row("th")}</thead><tbody>${body}</tbody></table><p><br></p>`;
};

const BLOCK_OPTIONS: { value: BlockTag; label: string }[] = [
  { value: "p", label: "Text" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "blockquote", label: "Quote" },
  { value: "pre", label: "Code block" },
];

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const closestEl = (node: Node | null, selector: string): HTMLElement | null => {
  if (!node) return null;
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
  return el ? el.closest(selector) : null;
};

/** Replace an element with its children, keeping the text in place. */
const unwrap = (el: HTMLElement) => {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
};

/** Drop every text-colour token inside `root` (not on `root` itself). */
const stripInk = (root: HTMLElement) => {
  (Array.from(root.querySelectorAll("[data-color]")) as HTMLElement[]).forEach((el) => {
    el.removeAttribute("data-color");
    if (el.tagName === "SPAN" && !el.attributes.length) unwrap(el);
  });
};

// A colour no one would pick, handed to execCommand("foreColor") only so the
// elements it creates can be found again and rewritten as palette tokens.
const INK_SENTINEL = "#010203";
const SENTINEL_RE = /rgb\(\s*1\s*,\s*2\s*,\s*3\s*\)|#010203/i;

/**
 * Turn whatever execCommand("foreColor", INK_SENTINEL) produced — a styled
 * <span> on modern engines, a <font color> on older ones — into
 * `<span data-color="…">`. Anything missed is harmless: the sanitizer drops
 * inline styles and unwraps <font>, so a stray sentinel never persists.
 */
const tokenizeSentinel = (root: HTMLElement, color: string) => {
  (Array.from(root.querySelectorAll("span[style],font[color]")) as HTMLElement[]).forEach((el) => {
    const marker = el.getAttribute("color") || el.style.color || "";
    if (!SENTINEL_RE.test(marker)) return;
    if (el.tagName === "FONT") {
      const span = document.createElement("span");
      while (el.firstChild) span.appendChild(el.firstChild);
      span.setAttribute("data-color", color);
      el.replaceWith(span);
      stripInk(span);
      return;
    }
    el.style.removeProperty("color");
    if (!el.getAttribute("style")) el.removeAttribute("style");
    el.setAttribute("data-color", color);
    stripInk(el);
  });
};

const queryState = (cmd: string): boolean => {
  try {
    return document.queryCommandState(cmd);
  } catch {
    return false;
  }
};

// Toolbar button. onMouseDown is cancelled so clicking never steals the
// selection from the body — execCommand needs it to still be there.
function Tool({
  label, active, onClick, children, disabled, testId,
}: { label: string; active?: boolean; onClick: () => void; children: React.ReactNode; disabled?: boolean; testId?: string }) {
  return (
    <button
      type="button"
      className={`nm-tool${active ? " is-active" : ""}`}
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      data-testid={testId}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function NoteEditor({
  note,
  saveState,
  tagSuggestions,
  onChange,
  onMeta,
  onAction,
  onSaveNow,
  onBack,
  readOnly = false,
  autoFocusTitle = false,
}: NoteEditorProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  // Table cell holding the caret — it shows its formula, not the value.
  const editingCellRef = useRef<HTMLTableCellElement | null>(null);
  // A column drag in flight (resize or reorder) — see the column section.
  const colDragRef = useRef<{
    mode: "resize" | "move";
    table: HTMLTableElement;
    /** Index the dragged column is at right now; a reorder moves it live. */
    index: number;
    startX: number;
    moved: boolean;
  } | null>(null);
  // Body HTML as last seeded/emitted, so a round-trip (enter a formula cell,
  // leave it unchanged) doesn't count as an edit.
  const lastHtmlRef = useRef("");
  // The column the Table popover is working on. Clicking into its filter box
  // takes focus out of the body, so by the time Filter is pressed there is no
  // caret left to read: the block acts on what it saw when the popover opened.
  const colTargetRef = useRef<ColumnTarget | null>(null);
  // The cell range that is selected, and a drag in flight building one. Both
  // are refs: the range is painted straight onto the cells, so picking one
  // never needs a re-render.
  const cellRangeRef = useRef<CellRange | null>(null);
  const cellDragRef = useRef<{ table: HTMLTableElement; anchor: { row: number; col: number } } | null>(null);

  const [title, setTitle] = useState(note.title || "");
  const [fmt, setFmt] = useState<FormatState>(EMPTY_FORMAT);
  const [bodyEmpty, setBodyEmpty] = useState(() => isBlankHtml(note.content || ""));
  const [words, setWords] = useState(() => countWords(htmlToText(note.content || "")));
  const [tagDraft, setTagDraft] = useState("");
  const [openPop, setOpenPop] = useState<null | "color" | "ink" | "more" | "link" | "table" | "history">(null);
  // Edit log for the History popover: null = loading.
  const [history, setHistory] = useState<NoteEditEntry[] | "error" | null>(null);
  // Pin / colour / tags / archive / share / trash are the owner's; a
  // colleague a note is shared with edits the title and body only.
  const ownerControls = !readOnly && !note.is_shared;
  const [linkUrl, setLinkUrl] = useState("");
  const [tableSize, setTableSize] = useState({ rows: "3", cols: "3" });
  // Formula bar: the caret cell's address and its formula (if any).
  const [cellInfo, setCellInfo] = useState<{ addr: string; formula: string | null } | null>(null);
  // The Table popover's sort/filter block, read from the caret's column when
  // the popover opens and again after each of its actions.
  const [colState, setColState] = useState<ColumnState | null>(null);
  const [filterDraft, setFilterDraft] = useState("");
  // Rows hidden by a filter anywhere in the body, so the bar above the body
  // can say so even in a read-only view, where there is no caret to follow.
  const [hiddenRows, setHiddenRows] = useState(0);

  const swatch = swatchFor(note.color);

  // ── Mount: seed the body once, optionally focus the title ────────────
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el) {
      el.innerHTML = sanitizeHtml(note.content || "");
      // Fresh values (and heals a note saved while a formula was on screen).
      recalcTables(el, null);
      lastHtmlRef.current = serializeBody(el);
      setHiddenRows(el.querySelectorAll("tr[data-filtered]").length);
    }
    if (autoFocusTitle && titleRef.current) titleRef.current.focus();
    // Only on mount — the parent remounts us per note via key={note.id}.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Close popovers on outside click / Escape ─────────────────────────
  useEffect(() => {
    if (!openPop) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".nm-pop-wrap")) setOpenPop(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenPop(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openPop]);

  // ── Toolbar state follows the caret ──────────────────────────────────
  const refreshFormat = useCallback(() => {
    const body = bodyRef.current;
    const sel = window.getSelection();
    if (!body || !sel || !sel.anchorNode || !body.contains(sel.anchorNode)) return;
    const li = closestEl(sel.anchorNode, "li");
    const listEl = li?.parentElement;
    const blockEl = closestEl(sel.anchorNode, "h1,h2,h3,blockquote,pre");
    const block = (blockEl?.tagName.toLowerCase() as BlockTag) || "p";
    const inTable = !!closestEl(sel.anchorNode, "table");
    const inked = closestEl(sel.anchorNode, "[data-color]");
    const filled = closestEl(sel.anchorNode, "[data-fill]");
    setFmt({
      bold: queryState("bold"),
      italic: queryState("italic"),
      underline: queryState("underline"),
      strike: queryState("strikeThrough"),
      ul: !!listEl && listEl.tagName === "UL" && !listEl.hasAttribute("data-checklist"),
      ol: !!listEl && listEl.tagName === "OL",
      checklist: !!listEl && listEl.tagName === "UL" && listEl.hasAttribute("data-checklist"),
      table: inTable,
      block,
      ink: (inked?.getAttribute("data-color") as NoteInkKey) || null,
      fill: (filled?.getAttribute("data-fill") as NoteInkKey) || null,
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshFormat);
    return () => document.removeEventListener("selectionchange", refreshFormat);
  }, [refreshFormat]);

  // ── Change plumbing ──────────────────────────────────────────────────
  const emitBody = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    // Keep every checklist item explicit so a freshly split <li> (Enter)
    // never inherits "checked" from the line it was born from.
    el.querySelectorAll("ul[data-checklist] > li").forEach((li) => {
      if (!li.hasAttribute("data-checked")) li.setAttribute("data-checked", "false");
    });
    // Dependent formula cells follow every edit live.
    recalcTables(el, editingCellRef.current);
    const html = serializeBody(el);
    lastHtmlRef.current = html;
    setHiddenRows(el.querySelectorAll("tr[data-filtered]").length);
    setBodyEmpty(isBlankHtml(html));
    setWords(countWords(htmlToText(html)));
    onChange({ content: html });
  }, [onChange]);

  const exec = useCallback((cmd: string, value?: string) => {
    if (readOnly) return;
    bodyRef.current?.focus();
    try {
      document.execCommand(cmd, false, value);
    } catch {
      /* unsupported command — nothing to do */
    }
    emitBody();
    refreshFormat();
  }, [emitBody, readOnly, refreshFormat]);

  const setBlock = (tag: BlockTag) => exec("formatBlock", `<${tag}>`);

  const toggleChecklist = useCallback(() => {
    if (readOnly) return;
    const body = bodyRef.current;
    if (!body) return;
    body.focus();
    const sel = window.getSelection();
    let li = closestEl(sel?.anchorNode || null, "li");
    let list = li?.parentElement || null;
    if (list && list.tagName === "UL" && list.hasAttribute("data-checklist")) {
      // Checklist → plain bullets.
      list.removeAttribute("data-checklist");
      list.querySelectorAll(":scope > li").forEach((item) => item.removeAttribute("data-checked"));
      emitBody();
      refreshFormat();
      return;
    }
    if (!list || (list.tagName !== "UL" && list.tagName !== "OL")) {
      try {
        document.execCommand("insertUnorderedList");
      } catch {
        return;
      }
      li = closestEl(window.getSelection()?.anchorNode || null, "li");
      list = li?.parentElement || null;
    }
    if (list && body.contains(list)) {
      if (list.tagName === "OL") {
        // Swap the ordered list for an unordered one, keeping the items.
        const ul = document.createElement("ul");
        while (list.firstChild) ul.appendChild(list.firstChild);
        list.replaceWith(ul);
        list = ul;
      }
      list.setAttribute("data-checklist", "true");
      list.querySelectorAll(":scope > li").forEach((item) => {
        if (!item.hasAttribute("data-checked")) item.setAttribute("data-checked", "false");
      });
    }
    emitBody();
    refreshFormat();
  }, [emitBody, readOnly, refreshFormat]);

  const toggleItem = useCallback((li: HTMLElement) => {
    li.setAttribute("data-checked", li.getAttribute("data-checked") === "true" ? "false" : "true");
    emitBody();
  }, [emitBody]);

  // ── Tables ───────────────────────────────────────────────────────────
  const placeCaret = (cell: HTMLElement) => {
    const range = document.createRange();
    range.selectNodeContents(cell);
    range.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const insertTable = (rows: number, cols: number) => {
    setOpenPop(null);
    const body = bodyRef.current;
    if (readOnly || !body) return;
    body.focus();
    const sel = window.getSelection();
    const saved = savedRangeRef.current;
    if (sel && saved && body.contains(saved.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(saved);
    }
    // Never nest a table inside a table cell: hop to just after the table.
    const inside = closestEl(sel?.anchorNode || null, "table");
    if (inside && sel) {
      const range = document.createRange();
      range.setStartAfter(inside);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    exec("insertHTML", tableHtml(rows, cols));
    const first = Array.from(body.querySelectorAll("table")).pop()?.querySelector("th,td") as HTMLElement | null;
    if (first) placeCaret(first);
  };

  /** The cell the caret is in (own body only). */
  const currentCell = (): HTMLTableCellElement | null => {
    const cell = closestEl(window.getSelection()?.anchorNode || null, "td,th") as HTMLTableCellElement | null;
    return cell && bodyRef.current?.contains(cell) ? cell : null;
  };

  const blankCell = (tag: "td" | "th") => {
    const el = document.createElement(tag);
    el.innerHTML = "<br>";
    return el;
  };

  const tableDelete = () => {
    const table = currentCell()?.closest("table");
    if (!table) return;
    const p = document.createElement("p");
    p.innerHTML = "<br>";
    table.replaceWith(p);
    placeCaret(p);
    emitBody();
  };

  // Every one of the four below moves refs with the cells: a formula that
  // pointed at a row or column keeps pointing at it once it has shifted along,
  // and one that pointed at a row or column that is now GONE says #REF!
  // (rewriteTableRefs → rewriteFormulaRefs). The maps are built from the
  // BEFORE geometry, so they are worked out before the DOM changes.

  const tableAddRow = () => {
    const cell = currentCell();
    const tr = cell?.parentElement as HTMLTableRowElement | null;
    const table = tr?.closest("table") as HTMLTableElement | null;
    if (!cell || !tr || !table) return;
    const fresh = document.createElement("tr");
    for (let i = 0; i < tr.children.length; i += 1) fresh.appendChild(blankCell("td"));
    // A header row grows the table body, not the header.
    const tbody = tr.parentElement?.tagName === "THEAD" ? table.querySelector("tbody") : null;
    const was = table.rows.length;
    if (tbody) tbody.insertBefore(fresh, tbody.firstChild);
    else tr.after(fresh);
    rewriteTableRefs(table, { rows: insertIndexMap(was, fresh.rowIndex) });
    placeCaret(fresh.children[Math.min(cell.cellIndex, fresh.children.length - 1)] as HTMLElement);
    emitBody();
  };

  const tableAddColumn = () => {
    const cell = currentCell();
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!cell || !table) return;
    const at = cell.cellIndex;
    const was = table.rows[0]?.cells.length || 0;
    table.querySelectorAll("tr").forEach((row) => {
      const ref = row.children[at] as HTMLElement | undefined;
      const fresh = blankCell(ref?.tagName === "TH" ? "th" : "td");
      if (ref) ref.after(fresh);
      else row.appendChild(fresh);
    });
    rewriteTableRefs(table, { cols: insertIndexMap(was, at + 1) });
    placeCaret((cell.parentElement as HTMLTableRowElement).children[at + 1] as HTMLElement);
    emitBody();
  };

  const tableDeleteRow = () => {
    const cell = currentCell();
    const tr = cell?.parentElement as HTMLTableRowElement | null;
    const table = tr?.closest("table") as HTMLTableElement | null;
    if (!tr || !table) return;
    const rows = Array.from(table.querySelectorAll("tr"));
    if (rows.length <= 1) return tableDelete();
    const next = rows[rows.indexOf(tr) + 1] || rows[rows.indexOf(tr) - 1];
    const map = deleteIndexMap(table.rows.length, tr.rowIndex);
    tr.remove();
    rewriteTableRefs(table, { rows: map });
    if (next) placeCaret(next.children[0] as HTMLElement);
    emitBody();
  };

  const tableDeleteColumn = () => {
    const cell = currentCell();
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!cell || !table) return;
    const at = cell.cellIndex;
    if ((cell.parentElement?.children.length || 0) <= 1) return tableDelete();
    const map = deleteIndexMap(table.rows[0]?.cells.length || 0, at);
    table.querySelectorAll("tr").forEach((row) => row.children[at]?.remove());
    rewriteTableRefs(table, { cols: map });
    const row = table.querySelector("tr");
    const target = row?.children[Math.min(at, row.children.length - 1)] as HTMLElement | undefined;
    if (target) placeCaret(target);
    emitBody();
  };

  /** Tab in a cell moves to the next one; on the last cell it adds a row.
   *  A row the filter hides is display:none, so the caret steps over it. */
  const tableTab = (backwards: boolean): boolean => {
    const cell = currentCell();
    if (!cell) return false;
    const table = cell.closest("table")!;
    const cells = (Array.from(table.querySelectorAll("th,td")) as HTMLElement[])
      .filter((c) => c === cell || !c.parentElement?.hasAttribute("data-filtered"));
    const i = cells.indexOf(cell);
    if (backwards) {
      if (i > 0) placeCaret(cells[i - 1]);
      return true;
    }
    if (i < cells.length - 1) placeCaret(cells[i + 1]);
    else tableAddRow();
    return true;
  };

  /** Enter in a cell moves down a row (Excel); the last row grows one. */
  const tableEnter = (): boolean => {
    const cell = currentCell();
    if (!cell) return false;
    const table = cell.closest("table") as HTMLTableElement;
    const rowIndex = (cell.parentElement as HTMLTableRowElement).rowIndex;
    const below = Array.from(table.rows)
      .slice(rowIndex + 1)
      .find((tr) => !tr.hasAttribute("data-filtered"));
    if (below) placeCaret(below.cells[Math.min(cell.cellIndex, below.cells.length - 1)]);
    else tableAddRow();
    return true;
  };

  // ── Column width and order ───────────────────────────────────────────
  // Resizing writes a `data-w` bucket on the header cell (see COL_W_STEP);
  // reordering moves the whole cell in every row, so the column's widths,
  // fills and formulas travel with it.
  //
  // REFS MOVE WITH THE COLUMN: "=SUM(B2:B5)" follows the column that was B
  // to wherever it lands, so the formula keeps adding up the same numbers,
  // and a formula pointing at a column the move shifted sideways follows that
  // one too. A live drag rewrites a column at a time, which composes — drag a
  // column out and back and the refs are the ones you started with.

  /** Move column `from` to `to` in every row of `table`, refs and all. */
  const moveColumn = (table: HTMLTableElement, from: number, to: number) => {
    if (from === to) return;
    const map = moveIndexMap(table.rows[0]?.cells.length || 0, from, to);
    Array.from(table.rows).forEach((row) => {
      const cells = Array.from(row.cells);
      const cell = cells[from];
      if (!cell) return;
      const ref = cells[to];
      if (!ref) row.appendChild(cell);
      else if (to > from) ref.after(cell);
      else ref.before(cell);
    });
    rewriteTableRefs(table, { cols: map });
  };

  /** Index of the column whose left half `x` falls in — the drop target. */
  const columnAt = (table: HTMLTableElement, x: number): number => {
    const cells = Array.from(table.rows[0]?.cells || []);
    for (let i = 0; i < cells.length; i += 1) {
      const rect = cells[i].getBoundingClientRect();
      if (x < rect.left + rect.width / 2) return i;
    }
    return Math.max(0, cells.length - 1);
  };

  /** Width buckets only count on the first row under table-layout: fixed. */
  const headerCell = (table: HTMLTableElement, index: number) =>
    (table.rows[0]?.cells[index] as HTMLTableCellElement | undefined) || null;

  const onColPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (readOnly || e.button !== 0 || colDragRef.current) return;
    const th = closestEl(e.target as HTMLElement, "th") as HTMLTableCellElement | null;
    const table = th?.closest("table") as HTMLTableElement | null;
    if (!th || !table || !bodyRef.current?.contains(table)) return;
    // Only a cell of the first row carries a width, and only that row's
    // cells are the handles — a th in a later row is just a cell.
    if (table.rows[0]?.cells[th.cellIndex] !== th) return;
    const rect = th.getBoundingClientRect();
    const mode = rect.right - e.clientX <= COL_RESIZE_ZONE ? "resize"
      : e.clientX - rect.left <= COL_GRIP_ZONE ? "move"
        : null;
    if (!mode) return;
    // Stop the press from placing a caret / starting a text selection; the
    // caret is put back by hand if the press turns out to be a plain click.
    e.preventDefault();
    colDragRef.current = { index: th.cellIndex, mode, moved: false, startX: e.clientX, table };
    bodyRef.current.classList.add("is-col-drag");
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* no pointer capture (jsdom, older engines) — the listeners still fire */
    }
  };

  const onColPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = colDragRef.current;
    if (!drag) return;
    if (!drag.moved && Math.abs(e.clientX - drag.startX) < COL_DRAG_SLOP) return;
    drag.moved = true;
    e.preventDefault();
    if (drag.mode === "resize") {
      const th = headerCell(drag.table, drag.index);
      const tableWidth = drag.table.getBoundingClientRect().width;
      if (!th || !tableWidth) return;
      const pct = ((e.clientX - th.getBoundingClientRect().left) / tableWidth) * 100;
      th.setAttribute("data-w", String(widthBucket(pct)));
      return;
    }
    // Reorder live, a column at a time, so the header follows the finger.
    const to = columnAt(drag.table, e.clientX);
    if (to === drag.index) return;
    moveColumn(drag.table, drag.index, to);
    drag.index = to;
  };

  const onColPointerUp = () => {
    const drag = colDragRef.current;
    if (!drag) return;
    colDragRef.current = null;
    bodyRef.current?.classList.remove("is-col-drag");
    if (!drag.moved) {
      // A plain click in a handle zone: give the caret back to the cell.
      const th = headerCell(drag.table, drag.index);
      if (th) {
        bodyRef.current?.focus();
        placeCaret(th);
      }
      return;
    }
    emitBody();
  };

  /** Menu equivalents of the drag, for a keyboard or a small screen. */
  const tableMoveColumn = (delta: number) => {
    const cell = currentCell();
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!cell || !table) return;
    const to = cell.cellIndex + delta;
    if (to < 0 || to >= (table.rows[0]?.cells.length || 0)) return;
    moveColumn(table, cell.cellIndex, to);
    placeCaret(cell);
    emitBody();
  };

  const tableResetWidths = () => {
    const table = currentCell()?.closest("table");
    if (!table) return;
    table.querySelectorAll("[data-w]").forEach((el) => el.removeAttribute("data-w"));
    emitBody();
  };

  // ── Cell ranges: select, copy, cut, paste ────────────────────────────
  // A rectangle of cells, the way a spreadsheet does it. Drag across cells,
  // Shift+click a cell, or press Ctrl/Cmd+A inside a table.
  //
  //  • COPY and CUT write both clipboard flavours: text/plain as TSV, the
  //    convention every spreadsheet reads, and text/html as a real table, so
  //    the grid survives a paste into another note. A formula cell puts its
  //    VALUE on the clipboard, never its text.
  //  • CUT then empties those cells — text, data-formula, data-fill and
  //    data-color — and leaves the grid plain: the same number of cells in
  //    every row. A header keeps data-w and data-sort, which describe the
  //    column rather than what the cell holds.
  //  • PASTE into a cell FILLS that cell and the cells right of and below it,
  //    growing the table when the pasted grid runs past an edge. It never
  //    nests: cells are filled with cell contents, and the sanitizer has
  //    already unwrapped any table that sat inside a cell.
  //
  // FORMULAS SURVIVE A PASTE FROM A NOTEMAKER TABLE, translated. The copy
  // writes the cell its grid started at on the clipboard table
  // (data-nm-origin, which no sanitizer keeps — see gridOrigin), so the paste
  // knows the (row, column) delta from there to where the grid is landing and
  // moves every ref by it: "=SUM(A1:A2)" copied one column right is
  // "=SUM(B1:B2)", and a "$" half stays where it is. A ref that lands off the
  // top or left edge becomes #REF!, like Excel.
  //
  // A payload with NO origin — Excel, Sheets, a web page, anything — lands as
  // values: its data-formula, if it somehow has one, is dropped rather than
  // read as a ref into a table it knows nothing about. An overwritten cell
  // always loses its own formula along with the value that formula produced,
  // whichever kind of payload lands on it.
  //
  // The cell text on the clipboard is still the computed VALUE, so pasting
  // into Excel, a text field or a note's paragraph gives numbers.

  const paintCellRange = () => {
    const body = bodyRef.current;
    if (!body) return;
    body.querySelectorAll(`.${CELL_SEL}`).forEach((cell) => {
      cell.classList.remove(CELL_SEL);
      if (!cell.getAttribute("class")) cell.removeAttribute("class");
    });
    const range = cellRangeRef.current;
    if (!range || !body.contains(range.table)) return;
    rangeCells(range).forEach((row) => row.forEach((cell) => cell.classList.add(CELL_SEL)));
  };

  const setCellRange = (range: CellRange | null) => {
    cellRangeRef.current = range;
    paintCellRange();
  };

  /** The range's values as the clipboard carries them: a formula cell gives
   *  its computed value, even the cell the caret is in, which is showing its
   *  formula on screen. Evaluated on a copy of the grid, so nothing moves. */
  const rangeValues = (range: CellRange): string[][] => {
    const cells = Array.from(range.table.rows).map((row) => Array.from(row.cells));
    const computed = cells.some((row) => row.some((c) => c.hasAttribute("data-formula")))
      ? evaluateTable(cells.map((row) => row.map((c) => ({
        formula: c.getAttribute("data-formula"),
        text: c.textContent || "",
      }))))
      : null;
    const { r0, r1, c0, c1 } = rangeRect(range);
    const out: string[][] = [];
    for (let r = r0; r <= r1; r += 1) {
      if (!cells[r]) continue;
      const row: string[] = [];
      for (let c = c0; c <= c1; c += 1) {
        if (!cells[r][c]) continue;
        row.push(computed?.[r]?.[c] ?? (cells[r][c].textContent || ""));
      }
      if (row.length) out.push(row);
    }
    return out;
  };

  /** Empty every cell of a range, keeping the grid plain. */
  const clearRangeCells = (range: CellRange) => {
    rangeCells(range).forEach((row) => row.forEach((cell) => {
      cell.innerHTML = "<br>";
      cell.removeAttribute("data-formula");
      cell.removeAttribute("data-fill");
      cell.removeAttribute("data-color");
    }));
  };

  /** Copy (or cut) the picked range. With no range the browser's own copy is
   *  left alone, so copying ordinary text still works. */
  const copyCellRange = (e: React.ClipboardEvent<HTMLDivElement>, cut: boolean) => {
    const range = cellRangeRef.current;
    if (!range || !bodyRef.current?.contains(range.table) || (cut && readOnly)) return;
    const cells = rangeCells(range);
    const values = rangeValues(range);
    if (!cells.length || !values.length) return;
    e.preventDefault();
    const rows = cells.map((row, r) => `<tr>${row.map((cell, c) => {
      const tag = cell.tagName === "TH" ? "th" : "td";
      // data-formula travels so a paste back into a table can translate it;
      // the cell TEXT is the value either way, so a paste that cannot
      // translate (no origin, or another app) still shows a number.
      const attrs = (["data-fill", "data-color", "data-formula"] as const)
        .map((name) => (cell.getAttribute(name) ? ` ${name}="${escapeHtml(cell.getAttribute(name) || "")}"` : ""))
        .join("");
      // A formula cell copies its value; any other cell copies its own markup,
      // so bold text and links survive the trip.
      const inner = cell.hasAttribute("data-formula") ? escapeHtml(values[r][c] || "") : cell.innerHTML;
      return `<${tag}${attrs}>${inner || "<br>"}</${tag}>`;
    }).join("")}</tr>`);
    // A range that starts on the header row keeps its <thead>, so pasting it
    // into a paragraph gives the shape the Table tool makes.
    const head = rangeRect(range).r0 === 0 && cells[0].every((cell) => cell.tagName === "TH")
      ? rows.shift()
      : null;
    const { r0, c0 } = rangeRect(range);
    e.clipboardData.setData("text/plain", cellsToTsv(values));
    e.clipboardData.setData(
      "text/html",
      `<table ${ORIGIN_ATTR}="${r0},${c0}">${head ? `<thead>${head}</thead>` : ""}${rows.length ? `<tbody>${rows.join("")}</tbody>` : ""}</table>`,
    );
    if (!cut) return;
    clearRangeCells(range);
    emitBody();
  };

  /** Fill `target` and the cells right of and below it from `grid`, growing
   *  the table when the grid runs past its right or bottom edge. Gives back
   *  the rectangle it filled.
   *
   *  `origin` is the cell the grid was copied FROM (gridOrigin); with it, a
   *  pasted formula is translated by the delta from there to here, and
   *  without it formulas are dropped and only values land. */
  const pasteGrid = (
    target: HTMLTableCellElement,
    grid: PasteCell[][],
    origin: { row: number; col: number } | null = null,
  ): CellRange | null => {
    const table = target.closest("table") as HTMLTableElement | null;
    if (!table || !grid.length) return null;
    const { row: r0, col: c0 } = cellCoord(target);
    const width = grid.reduce((w, row) => Math.max(w, row.length), 0);
    // Grow down with body rows as wide as the table. A row a filter hides
    // counts as a row: the saved order is the real one, the same reason a
    // formula still sees a hidden row.
    const host = table.querySelector("tbody") || table;
    while (table.rows.length < r0 + grid.length) {
      const fresh = document.createElement("tr");
      const cols = Math.max(table.rows[0]?.cells.length || 0, c0 + width);
      for (let i = 0; i < cols; i += 1) fresh.appendChild(blankCell("td"));
      host.appendChild(fresh);
    }
    // Grow right: every row gains cells, and a row of header cells gains
    // header cells, so the grid stays plain.
    Array.from(table.rows).forEach((row) => {
      while (row.cells.length < c0 + width) {
        row.appendChild(blankCell(row.cells[0]?.tagName === "TH" ? "th" : "td"));
      }
    });
    grid.forEach((cells, r) => {
      const row = table.rows[r0 + r];
      cells.forEach((value, c) => {
        const cell = row?.cells[c0 + c];
        if (!cell) return;
        // A pasted value REPLACES the cell: the old formula goes with the old
        // value it produced, and the pasted cell's own colours win.
        cell.innerHTML = value.html;
        const moved = value.formula && origin
          ? rewriteFormulaRefs(value.formula, { dc: c0 - origin.col, dr: r0 - origin.row })
          : null;
        if (moved) cell.setAttribute("data-formula", moved);
        else cell.removeAttribute("data-formula");
        (["data-fill", "data-color"] as const).forEach((name) => {
          const token = name === "data-fill" ? value.fill : value.color;
          if (token) cell.setAttribute(name, token);
          else cell.removeAttribute(name);
        });
      });
    });
    return { anchor: { col: c0, row: r0 }, head: { col: c0 + width - 1, row: r0 + grid.length - 1 }, table };
  };

  /** Ctrl/Cmd+A inside a table picks the whole grid. Pressing it again with
   *  the whole grid already picked gives up and lets the browser select the
   *  note, so there is still a way to select everything from a cell. */
  const selectTableCells = (): boolean => {
    const cell = currentCell();
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!table) return false;
    const rows = table.rows.length;
    const cols = table.rows[0]?.cells.length || 0;
    if (!rows || !cols) return false;
    const range = cellRangeRef.current;
    const rect = range && range.table === table ? rangeRect(range) : null;
    if (rect && rect.r0 === 0 && rect.c0 === 0 && rect.r1 >= rows - 1 && rect.c1 >= cols - 1) return false;
    setCellRange({ anchor: { col: 0, row: 0 }, head: { col: cols - 1, row: rows - 1 }, table });
    return true;
  };

  /** A press in a cell starts a range drag — but it only becomes one once the
   *  pointer reaches a SECOND cell, so selecting text inside a single cell
   *  still works. The column drag above gets first refusal on the press.
   *
   *  A FINGER never drags a range out: on a touch screen the same gesture is
   *  how the view is scrolled, and taking it over would trap a reader inside
   *  a wide table. The phone's editor has no range selection at all for the
   *  same reason; a mouse or a pen has no such conflict. */
  const onBodyPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    onColPointerDown(e);
    if (colDragRef.current || readOnly || e.button !== 0 || e.pointerType === "touch") return;
    const cell = closestEl(e.target as HTMLElement, "td,th") as HTMLTableCellElement | null;
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!cell || !table || !bodyRef.current?.contains(table)) {
      setCellRange(null);
      return;
    }
    const range = cellRangeRef.current;
    if (e.shiftKey) {
      // Shift+click extends from the range's anchor, or from the caret's cell
      // when there is no range yet.
      const caret = currentCell();
      const anchor = range && range.table === table
        ? range.anchor
        : caret && caret.closest("table") === table ? cellCoord(caret) : cellCoord(cell);
      e.preventDefault();
      setCellRange({ anchor, head: cellCoord(cell), table });
      return;
    }
    cellDragRef.current = { anchor: cellCoord(cell), table };
    if (range) setCellRange(null);
  };

  const onBodyPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    onColPointerMove(e);
    const drag = cellDragRef.current;
    if (colDragRef.current || !drag) return;
    const cell = closestEl(e.target as HTMLElement, "td,th") as HTMLTableCellElement | null;
    if (!cell || cell.closest("table") !== drag.table) return;
    const head = cellCoord(cell);
    const started = !!cellRangeRef.current;
    // Still inside the cell the press landed in: leave the browser's own text
    // selection to it.
    if (!started && head.row === drag.anchor.row && head.col === drag.anchor.col) return;
    e.preventDefault();
    if (!started) {
      // Taking over: collapse the text selection the press started and stop
      // the browser extending it under the finger (see .is-cell-drag).
      bodyRef.current?.classList.add("is-cell-drag");
      const from = drag.table.rows[drag.anchor.row]?.cells[drag.anchor.col];
      if (from) placeCaret(from);
    }
    setCellRange({ anchor: drag.anchor, head, table: drag.table });
  };

  const onBodyPointerUp = () => {
    onColPointerUp();
    cellDragRef.current = null;
    bodyRef.current?.classList.remove("is-cell-drag");
  };

  // ── Column sort and filter ─────────────────────────────────────────
  // The note body IS the storage, so both of these live in the saved HTML:
  //
  //  • A sort PHYSICALLY REORDERS the <tr>s, so the order survives a save, a
  //    reload, Markdown export, the phone and a shared recipient. The sorted
  //    column's <th> carries data-sort as the indicator and the toggle memory.
  //  • A filter hides rows in place with data-filtered; the row keeps every
  //    cell, so clearing the filter is just dropping the attribute, and no
  //    save can lose a hidden row.
  //
  // A TABLE WITH FORMULAS SORTS LIKE ANY OTHER. The new row order is also a
  // ref map (orderIndexMap), so a row-local "=B2*C2" follows its row down to
  // row 5 and becomes "=B5*C5", while a whole-column "=SUM(B2:B5)" still
  // spans the same rows — the numbers are the same numbers, just in a new
  // order. Filtering needs no rewriting at all, since it moves nothing; but a
  // formula still sees the hidden rows, because there is no SUBTOTAL.

  const caretColumn = (): ColumnTarget | null => {
    const cell = currentCell();
    const table = cell?.closest("table") as HTMLTableElement | null;
    return cell && table ? { table, col: cell.cellIndex } : null;
  };

  /** The popover's column, as long as its table is still in the body. */
  const popColumn = (): ColumnTarget | null => {
    const target = colTargetRef.current;
    return target && bodyRef.current?.contains(target.table) ? target : null;
  };

  /** Point the popover's block at a column (the caret's, by default). */
  const refreshColumn = (target: ColumnTarget | null = caretColumn()) => {
    colTargetRef.current = target;
    setColState(columnStateFor(target));
  };

  const sortColumn = (dir: SortDir) => {
    const target = popColumn();
    if (readOnly || !target) return;
    const { table, col } = target;
    const rows = bodyRows(table);
    // Every body row has to share one parent (<tbody>, for a table this
    // editor made) or the move would shuffle rows between thead and tbody.
    const host = rows[0]?.parentElement;
    if (!host || rows.some((tr) => tr.parentElement !== host)) return;
    const order = sortRowOrder(columnValues(table, col), dir);
    order.forEach((i) => host.appendChild(rows[i]));
    // The header row does not move, hence the offset of 1.
    rewriteTableRefs(table, { rows: orderIndexMap(order, 1) });
    // One column at a time carries the indicator.
    table.querySelectorAll("[data-sort]").forEach((th) => th.removeAttribute("data-sort"));
    const header = table.rows[0]?.cells[col];
    // A pasted table whose first row is <td> keeps the new order, just no
    // arrow: the sanitizers allow data-sort on a header cell only.
    if (header?.tagName === "TH") header.setAttribute("data-sort", dir);
    placeCaret((header || rows[0]) as HTMLElement);
    emitBody();
    refreshColumn(target);
  };

  const filterColumn = (query: string, mode: FilterMode) => {
    const target = popColumn();
    if (readOnly || !target) return;
    const { table, col } = target;
    const values = columnValues(table, col);
    bodyRows(table).forEach((tr, i) => {
      if (cellMatchesFilter(values[i], query, mode)) tr.removeAttribute("data-filtered");
      else tr.setAttribute("data-filtered", "true");
    });
    setFilterDraft(query);
    emitBody();
    refreshColumn(target);
  };

  /** Show every row again — a filter's one undo, over a table or the body. */
  const clearFilter = (scope: Element | null) => {
    if (readOnly || !scope) return;
    scope.querySelectorAll("tr[data-filtered]").forEach((tr) => tr.removeAttribute("data-filtered"));
    setFilterDraft("");
    emitBody();
    refreshColumn(popColumn());
  };

  // ── Text colour and cell fill ────────────────────────────────────────
  // Both are palette TOKENS, never a hex: `data-color` for ink, `data-fill`
  // for a cell's background, mapped per theme in notemaker.css. On a table
  // cell the ink goes on the cell itself, not on a wrapper span, because
  // recalcTables rewrites a formula cell's textContent and would throw any
  // wrapper away.

  /** Every cell the selection touches; the caret's own cell if it is collapsed. */
  const selectedCells = (): HTMLTableCellElement[] => {
    const body = bodyRef.current;
    const sel = window.getSelection();
    // A picked cell range wins, so a fill or an ink covers the rectangle.
    const picked = cellRangeRef.current;
    if (picked && body?.contains(picked.table)) return rangeCells(picked).flat();
    if (!body || !sel || !sel.rangeCount) return [];
    if (sel.isCollapsed) {
      const cell = currentCell();
      return cell ? [cell] : [];
    }
    const range = sel.getRangeAt(0);
    return (Array.from(body.querySelectorAll("td,th")) as HTMLTableCellElement[])
      .filter((cell) => range.intersectsNode(cell));
  };

  const applyFill = (color: NoteInkKey | null) => {
    if (readOnly) return;
    const cells = selectedCells();
    if (!cells.length) return;
    cells.forEach((cell) => {
      if (color) cell.setAttribute("data-fill", color);
      else cell.removeAttribute("data-fill");
    });
    emitBody();
  };

  const applyInk = (color: NoteInkKey | null) => {
    if (readOnly) return;
    const body = bodyRef.current;
    const sel = window.getSelection();
    if (!body || !sel || !sel.rangeCount || !sel.anchorNode || !body.contains(sel.anchorNode)) return;

    // Whole cells: the attribute lives on the cell, and a selection spanning
    // several cells colours all of them.
    const cells = selectedCells();
    const wholeCells = cells.length > 1 || (cells.length === 1 && sel.isCollapsed);
    if (wholeCells) {
      cells.forEach((cell) => {
        if (color) cell.setAttribute("data-color", color);
        else cell.removeAttribute("data-color");
      });
      // Ink set on a cell wins over any span left inside it from earlier.
      cells.forEach((cell) => stripInk(cell));
      emitBody();
      refreshFormat();
      return;
    }

    body.focus();
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    if (!color) {
      // "No colour": drop every token the selection touches, inside a cell
      // or in a span wrapping part of a paragraph.
      (Array.from(body.querySelectorAll("[data-color]")) as HTMLElement[])
        .filter((el) => range.intersectsNode(el))
        .forEach((el) => {
          el.removeAttribute("data-color");
          if (el.tagName === "SPAN" && !el.attributes.length) unwrap(el);
        });
      emitBody();
      refreshFormat();
      return;
    }
    // Let the browser split the selection for us — hand-rolled range
    // wrapping mis-handles partly selected nodes and block boundaries — then
    // swap the inline colour it produced for our token. A nested token wins
    // over an outer one by being the element's own rule, so no cleanup first.
    try {
      document.execCommand("styleWithCSS", false, "true");
    } catch {
      /* older engines emit <font color> instead; handled below */
    }
    try {
      document.execCommand("foreColor", false, INK_SENTINEL);
    } catch {
      return;
    }
    tokenizeSentinel(body, color);
    emitBody();
    refreshFormat();
  };

  // ── Formula cells ────────────────────────────────────────────────────
  // Type "=…" in a cell and leave it (click away, Tab, Enter): the text
  // becomes the cell's data-formula and the cell shows the result. Coming
  // back into the cell shows the formula again for editing; typing a plain
  // value over it (or clearing it) drops the formula.
  const commitCell = (cell: HTMLTableCellElement) => {
    const text = (cell.textContent || "").replace(/ /g, " ").trim();
    if (text.length > 1 && text.startsWith("=")) {
      cell.setAttribute("data-formula", text.slice(0, FORMULA_MAX));
    } else {
      cell.removeAttribute("data-formula");
    }
  };

  const syncActiveCell = useCallback(() => {
    const body = bodyRef.current;
    if (readOnly || !body) return;
    const anchor = window.getSelection()?.anchorNode || null;
    const cell = anchor && body.contains(anchor)
      ? (closestEl(anchor, "td,th") as HTMLTableCellElement | null)
      : null;
    const prev = editingCellRef.current;
    if (cell === prev) {
      if (cell) {
        const typed = (cell.textContent || "").trim();
        setCellInfo({ addr: cellAddress(cell), formula: typed.startsWith("=") ? typed : cell.getAttribute("data-formula") });
      }
      return;
    }
    editingCellRef.current = cell;
    if (prev && body.contains(prev)) {
      commitCell(prev);
      recalcTables(body, cell);
      if (serializeBody(body) !== lastHtmlRef.current) emitBody();
    }
    if (cell) {
      const formula = cell.getAttribute("data-formula");
      if (formula && cell.textContent !== formula) {
        cell.textContent = formula;
        const range = document.createRange();
        range.selectNodeContents(cell);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      setCellInfo({ addr: cellAddress(cell), formula });
    } else {
      setCellInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitBody, readOnly]);

  useEffect(() => {
    document.addEventListener("selectionchange", syncActiveCell);
    return () => document.removeEventListener("selectionchange", syncActiveCell);
  }, [syncActiveCell]);

  // ── Body events ──────────────────────────────────────────────────────
  const handleBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const target = e.target as HTMLElement;
    const li = target.closest("li");
    if (li && li.parentElement?.matches("ul[data-checklist]") && bodyRef.current?.contains(li)) {
      const rect = li.getBoundingClientRect();
      if (e.clientX - rect.left < 30) {
        e.preventDefault();
        toggleItem(li);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (!html && !text) return;
    e.preventDefault();
    // The origin marker is read from the RAW html: sanitizing drops it, and a
    // pasted formula can only be translated if it is known where it came from.
    const origin = html ? gridOrigin(html) : null;
    const payload = html
      ? sanitizeHtml(html)
      : escapeHtml(text).replace(/\r?\n/g, "<br>");
    // Into a cell, a grid FILLS cells — see the cell-range block above. Tab and
    // newline text counts as a grid too, because that is the other half of what
    // a spreadsheet puts on the clipboard; rich text that is not a table keeps
    // its markup and lands in the cell as it always has.
    const cell = currentCell();
    const grid = cell ? (html ? gridFromHtml(payload) : gridFromText(text)) : null;
    if (cell && grid) {
      const filled = pasteGrid(cell, grid, origin);
      // While the caret is in a cell, that cell shows its FORMULA rather than
      // its value: recalcTables leaves it alone and commitCell reads the
      // formula back out of it on the way out. A formula landing under the
      // caret therefore has to show its text, or that commit would write the
      // pasted value over it.
      const landed = cell.getAttribute("data-formula");
      if (landed && cell.textContent !== landed) cell.textContent = landed;
      emitBody();
      if (filled) {
        placeCaret(cell);
        setCellRange(filled);
      }
      return;
    }
    // Anywhere else a pasted grid never nests. Like insertTable, hop to just
    // after the table the caret is in and drop it there; the row/column,
    // formula, sort and filter tools all assume a plain grid.
    //
    // A grid landing as a TABLE OF ITS OWN starts at A1, so its formulas move
    // by the delta from the cell they were copied from to that corner; a
    // payload with no origin has its data-formula dropped, because a ref read
    // out of a table this editor knows nothing about means nothing here.
    const body = retargetFormulas(payload, origin);
    const sel = window.getSelection();
    const inside = /<table[\s>]/i.test(body) ? closestEl(sel?.anchorNode || null, "table") : null;
    if (inside && sel && bodyRef.current?.contains(inside)) {
      const range = document.createRange();
      range.setStartAfter(inside);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    try {
      document.execCommand("insertHTML", false, body);
    } catch {
      document.execCommand("insertText", false, text);
    }
    emitBody();
  };

  const applyMarkdownShortcut = (): boolean => {
    const body = bodyRef.current;
    const sel = window.getSelection();
    if (!body || !sel || !sel.isCollapsed || !sel.anchorNode || !body.contains(sel.anchorNode)) return false;
    const block = closestEl(sel.anchorNode, "p,div,li,h1,h2,h3,blockquote,pre") || body;
    const range = document.createRange();
    range.setStart(block, 0);
    range.setEnd(sel.anchorNode, sel.anchorOffset);
    const before = range.toString().replace(/ /g, " ");
    const inList = block.tagName === "LI";
    let run: (() => void) | null = null;
    if (!inList && (before === "-" || before === "*")) run = () => exec("insertUnorderedList");
    else if (!inList && before === "1.") run = () => exec("insertOrderedList");
    else if (before === "[]" || before === "[ ]") run = toggleChecklist;
    else if (!inList && before === "#") run = () => setBlock("h1");
    else if (!inList && before === "##") run = () => setBlock("h2");
    else if (!inList && before === "###") run = () => setBlock("h3");
    else if (!inList && before === ">") run = () => setBlock("blockquote");
    if (!run) return false;
    range.deleteContents();
    run();
    return true;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const mod = e.ctrlKey || e.metaKey;
    // A picked cell range lives until a key moves the caret or changes
    // something: Ctrl/Cmd shortcuts (copy, cut, select-all) keep it, a
    // modifier on its own keeps it, Delete and Backspace empty it, and
    // anything else collapses it, the way a spreadsheet does.
    if (mod && e.key.toLowerCase() === "a" && selectTableCells()) {
      e.preventDefault();
      return;
    }
    if (cellRangeRef.current) {
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        clearRangeCells(cellRangeRef.current);
        emitBody();
        return;
      }
      if (!mod && !["Alt", "Control", "Meta", "Shift"].includes(e.key)) setCellRange(null);
    }
    if (e.key === " " && !mod && !e.shiftKey) {
      if (applyMarkdownShortcut()) e.preventDefault();
      return;
    }
    if (e.key === "Enter" && mod) {
      const li = closestEl(window.getSelection()?.anchorNode || null, "li");
      if (li && li.parentElement?.matches("ul[data-checklist]")) {
        e.preventDefault();
        toggleItem(li);
      }
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      // In a table cell Enter moves down (Shift+Enter still breaks a line).
      if (tableEnter()) {
        e.preventDefault();
        return;
      }
      const li = closestEl(window.getSelection()?.anchorNode || null, "li");
      if (li && li.parentElement?.matches("ul[data-checklist]")) {
        const wasChecked = li.getAttribute("data-checked") === "true";
        setTimeout(() => {
          const anchor = window.getSelection()?.anchorNode || null;
          // The browser clones the <li> (with data-checked) for the new line;
          // a new item always starts unchecked.
          const fresh = closestEl(anchor, "li");
          if (fresh && fresh !== li && fresh.parentElement?.matches("ul[data-checklist]")) {
            fresh.setAttribute("data-checked", "false");
            emitBody();
          }
          // A ticked item is drawn grey + struck-through by CSS. When Enter
          // splits away from it, Chrome "preserves" that look by wrapping the
          // new, still-empty line in <font color><strike>…</strike></font>, so
          // the next thing typed would come out struck. Reset the empty line.
          if (wasChecked) {
            const block = closestEl(anchor, "li,p,div,h1,h2,h3,blockquote,pre");
            if (block && block !== li && !(block.textContent || "").trim()
              && block.querySelector("font,strike,s,del,span[style]")) {
              block.innerHTML = "<br>";
              const range = document.createRange();
              range.setStart(block, 0);
              range.collapse(true);
              const sel = window.getSelection();
              sel?.removeAllRanges();
              sel?.addRange(range);
              emitBody();
            }
          }
        }, 0);
      }
      return;
    }
    if (e.key === "Tab") {
      if (tableTab(e.shiftKey)) {
        e.preventDefault();
        return;
      }
      const li = closestEl(window.getSelection()?.anchorNode || null, "li");
      if (li) {
        e.preventDefault();
        exec(e.shiftKey ? "outdent" : "indent");
      }
      return;
    }
    if (mod && e.key.toLowerCase() === "k") {
      e.preventDefault();
      openLink();
      return;
    }
    if (mod && e.shiftKey && e.key.toLowerCase() === "x") {
      e.preventDefault();
      exec("strikeThrough");
    }
  };

  // ── Link popover ─────────────────────────────────────────────────────
  const openLink = () => {
    if (readOnly) return;
    const sel = window.getSelection();
    savedRangeRef.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const existing = closestEl(sel?.anchorNode || null, "a");
    setLinkUrl(existing?.getAttribute("href") || "");
    setOpenPop("link");
  };

  const applyLink = () => {
    const raw = linkUrl.trim();
    setOpenPop(null);
    const body = bodyRef.current;
    if (!body) return;
    body.focus();
    const sel = window.getSelection();
    const range = savedRangeRef.current;
    if (sel && range) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
    if (!raw) {
      exec("unlink");
      return;
    }
    const url = /^(https?:\/\/|mailto:|tel:)/i.test(raw) ? raw : `https://${raw}`;
    if (!range || range.collapsed) {
      exec("insertHTML", `<a href="${escapeHtml(url)}">${escapeHtml(raw)}</a>&nbsp;`);
    } else {
      exec("createLink", url);
    }
  };

  // ── Edit history ─────────────────────────────────────────────────────
  const openHistory = async () => {
    setOpenPop("history");
    setHistory(null);
    try {
      // Save first so the list includes what was just typed.
      await onSaveNow();
      const res = await notesApi.history(note.id);
      setHistory(res.data.items || []);
    } catch {
      setHistory("error");
    }
  };

  // ── Tags ─────────────────────────────────────────────────────────────
  const commitTag = (raw: string) => {
    const tag = normalizeTag(raw);
    setTagDraft("");
    if (!tag || note.tags.includes(tag) || note.tags.length >= 20) return;
    onMeta({ tags: [...note.tags, tag] });
  };
  const removeTag = (tag: string) => onMeta({ tags: note.tags.filter((t) => t !== tag) });

  const onTagKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitTag(tagDraft);
    } else if (e.key === "Backspace" && !tagDraft && note.tags.length) {
      removeTag(note.tags[note.tags.length - 1]);
    } else if (e.key === "Escape") {
      setTagDraft("");
      (e.target as HTMLInputElement).blur();
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────
  const savePill = (() => {
    switch (saveState) {
      case "saving":
        return <span className="nm-save-pill is-saving"><Loader2 size={12} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> Saving…</span>;
      case "dirty":
        return <span className="nm-save-pill is-dirty" title={`${MOD}+S saves now`}>Unsaved changes</span>;
      case "error":
        return (
          <button type="button" className="nm-save-pill is-error" onClick={onSaveNow} title="Retry saving">
            <AlertCircle size={12} aria-hidden="true" /> Save failed · Retry
          </button>
        );
      case "saved":
        return <span className="nm-save-pill is-saved"><Check size={12} aria-hidden="true" /> Saved</span>;
      default:
        return note.updated_at ? (
          <button
            type="button"
            className="nm-save-pill is-link"
            onClick={() => void openHistory()}
            title="See who changed what"
          >
            Edited {relativeTime(note.updated_at)}{note.last_edited_by_name ? ` by ${note.last_edited_by_name}` : ""}
          </button>
        ) : null;
    }
  })();

  const menuItem = (label: string, icon: React.ReactNode, action: () => void, opts: { danger?: boolean; kbd?: string } = {}) => (
    <button
      type="button"
      role="menuitem"
      className={`nm-menu-item${opts.danger ? " is-danger" : ""}`}
      // Keep the body's selection: the table tools act on the caret's cell.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { setOpenPop(null); action(); }}
    >
      {icon} {label} {opts.kbd && <kbd>{opts.kbd}</kbd>}
    </button>
  );

  const tagListId = `nm-tags-${note.id}`;

  return (
    <section
      className="nm-editor gt-fade-in"
      style={{ ["--nm-tint" as any]: swatch.tint, ["--nm-stripe" as any]: swatch.stripe }}
      aria-label="Note editor"
    >
      <header className="nm-editor-head">
        <div className="nm-editor-head-left">
          <button type="button" className="gt-icon-btn is-sm nm-back-btn" onClick={onBack} aria-label="Back to notes">
            <ArrowLeft size={18} aria-hidden="true" />
          </button>
          {savePill}
        </div>
        <div className="nm-editor-head-right">
          <div className="nm-pop-wrap">
            <button
              type="button"
              className="gt-icon-btn is-sm"
              onClick={() => (openPop === "history" ? setOpenPop(null) : void openHistory())}
              aria-label="Edit history"
              aria-haspopup="dialog"
              aria-expanded={openPop === "history"}
              title="Edit history — who changed what"
              data-testid="note-history-btn"
            >
              <History size={17} aria-hidden="true" />
            </button>
            {openPop === "history" && (
              <div className="nm-pop nm-history-pop" role="dialog" aria-label="Edit history">
                <div className="nm-history-head">Edit history</div>
                {history === null ? (
                  <div className="nm-history-empty"><Loader2 size={15} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> Loading…</div>
                ) : history === "error" ? (
                  <div className="nm-history-empty">Couldn't load the history.</div>
                ) : history.length === 0 ? (
                  <div className="nm-history-empty">No edits recorded yet.</div>
                ) : (
                  <ol className="nm-history-list">
                    {history.map((h) => (
                      <li key={h.id} className="nm-history-item">
                        <div className="nm-history-who">
                          <span className="nm-history-name">{h.is_me ? "You" : h.user_name}</span>
                          <span className="nm-history-time" title={h.edited_at ? fullTimestamp(h.edited_at) : undefined}>
                            {relativeTime(h.edited_at)}
                          </span>
                        </div>
                        {h.summary.map((line, i) => <p key={i} className="nm-history-line">{line}</p>)}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
          {ownerControls && (
            <>
              <button
                type="button"
                className={`gt-icon-btn is-sm${note.is_pinned ? " is-active" : ""}`}
                style={note.is_pinned ? { color: "var(--gt-primary)", background: "var(--gt-primary-container)" } : undefined}
                onClick={() => onMeta({ is_pinned: !note.is_pinned })}
                aria-label={note.is_pinned ? "Unpin note" : "Pin note"}
                aria-pressed={note.is_pinned}
                title={note.is_pinned ? "Unpin" : "Pin to top"}
              >
                {note.is_pinned ? <PinOff size={17} aria-hidden="true" /> : <Pin size={17} aria-hidden="true" />}
              </button>
              <div className="nm-pop-wrap">
                <button
                  type="button"
                  className="gt-icon-btn is-sm"
                  onClick={() => setOpenPop(openPop === "color" ? null : "color")}
                  aria-label="Note colour"
                  aria-haspopup="true"
                  aria-expanded={openPop === "color"}
                  title="Colour"
                >
                  <Palette size={17} aria-hidden="true" />
                </button>
                {openPop === "color" && (
                  <div className="nm-pop" role="dialog" aria-label="Choose a colour">
                    <div className="nm-swatch-row" style={{ padding: 6 }}>
                      {NOTE_SWATCHES.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          className={`nm-swatch is-lg${note.color === s.key ? " is-active" : ""}`}
                          style={{ background: s.key === "default" ? "#fff" : s.tint, boxShadow: `inset 0 0 0 2px ${s.stripe}` }}
                          title={s.label}
                          aria-label={`${s.label} colour`}
                          aria-pressed={note.color === s.key}
                          onClick={() => { onMeta({ color: s.key as NoteColor }); setOpenPop(null); }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="nm-pop-wrap">
            <button
              type="button"
              className="gt-icon-btn is-sm"
              onClick={() => setOpenPop(openPop === "more" ? null : "more")}
              aria-label="Note actions"
              aria-haspopup="menu"
              aria-expanded={openPop === "more"}
              title="More"
            >
              <MoreHorizontal size={18} aria-hidden="true" />
            </button>
            {openPop === "more" && (
              <div className="nm-pop" role="menu" aria-label="Note actions" style={{ minWidth: 230 }}>
                {/* Closing the editor returns to the gallery — the list keeps
                    its scope/filters. Esc does the same from anywhere. */}
                {menuItem("Close note", <X size={15} aria-hidden="true" />, onBack, { kbd: "Esc" })}
                <div className="nm-menu-sep" />
                {note.is_shared ? (
                  <>
                    {menuItem("Make my own copy", <Copy size={15} aria-hidden="true" />, () => onAction("duplicate"))}
                    {menuItem("Copy as text", <Copy size={15} aria-hidden="true" />, () => onAction("copy"))}
                    {menuItem("Download as Markdown", <Download size={15} aria-hidden="true" />, () => onAction("download"))}
                    {menuItem("Print", <Printer size={15} aria-hidden="true" />, () => onAction("print"))}
                    <div className="nm-menu-sep" />
                    {menuItem("Send to Daily Planner", <CalendarPlus size={15} aria-hidden="true" />, () => onAction("planner"))}
                  </>
                ) : readOnly ? (
                  <>
                    {menuItem("Restore", <RotateCcw size={15} aria-hidden="true" />, () => onAction("restore"))}
                    {menuItem("Delete forever", <Trash2 size={15} aria-hidden="true" />, () => onAction("deleteForever"), { danger: true })}
                  </>
                ) : (
                  <>
                    {menuItem("Duplicate", <Copy size={15} aria-hidden="true" />, () => onAction("duplicate"))}
                    {menuItem("Copy as text", <Copy size={15} aria-hidden="true" />, () => onAction("copy"))}
                    {menuItem("Download as Markdown", <Download size={15} aria-hidden="true" />, () => onAction("download"))}
                    {menuItem("Print", <Printer size={15} aria-hidden="true" />, () => onAction("print"))}
                    <div className="nm-menu-sep" />
                    {menuItem(
                      note.shared_with.length ? `Share… (${note.shared_with.length})` : "Share…",
                      <Users size={15} aria-hidden="true" />,
                      () => onAction("share")
                    )}
                    {menuItem("Send to Daily Planner", <CalendarPlus size={15} aria-hidden="true" />, () => onAction("planner"))}
                    <div className="nm-menu-sep" />
                    {menuItem(
                      note.is_archived ? "Unarchive" : "Archive",
                      note.is_archived ? <ArchiveRestore size={15} aria-hidden="true" /> : <Archive size={15} aria-hidden="true" />,
                      () => onMeta({ is_archived: !note.is_archived })
                    )}
                    {menuItem("Move to Trash", <Trash2 size={15} aria-hidden="true" />, () => onAction("trash"), { danger: true })}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="gt-icon-btn is-sm nm-close-btn"
            onClick={onBack}
            aria-label="Close note"
            title="Close (Esc)"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      </header>

      {note.is_shared && (
        <div className="nm-banner is-shared is-slim" role="status">
          <Users size={14} aria-hidden="true" />
          <span>Shared by {note.owner_name || "a colleague"} · you can edit the title and text. Every change is recorded in the history.</span>
        </div>
      )}
      {readOnly && !note.is_shared && (
        <div className="nm-banner" role="status">
          <Trash2 size={16} aria-hidden="true" />
          <span>This note is in Trash{note.deleted_at ? ` · deleted ${relativeTime(note.deleted_at)}` : ""}. Restore it to keep editing.</span>
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
            <button type="button" className="gt-btn is-primary" onClick={() => onAction("restore")}>
              <RotateCcw size={14} aria-hidden="true" /> Restore
            </button>
            <button type="button" className="gt-btn is-danger" onClick={() => onAction("deleteForever")}>
              Delete forever
            </button>
          </span>
        </div>
      )}

      <input
        ref={titleRef}
        className="nm-title-input"
        value={title}
        placeholder="Untitled note"
        aria-label="Note title"
        maxLength={300}
        readOnly={readOnly}
        onChange={(e) => { setTitle(e.target.value); onChange({ title: e.target.value }); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            bodyRef.current?.focus();
          }
        }}
      />

      {!readOnly && (
        <div className="nm-toolbar" role="toolbar" aria-label="Formatting">
          <div className="nm-toolbar-group nm-toolbar-history">
            <Tool label={`Undo (${MOD}+Z)`} onClick={() => exec("undo")}><Undo2 size={16} aria-hidden="true" /></Tool>
            <Tool label={`Redo (${MOD}+Shift+Z)`} onClick={() => exec("redo")}><Redo2 size={16} aria-hidden="true" /></Tool>
          </div>
          <span className="nm-toolbar-sep" />
          <select
            className="nm-select nm-tool-select"
            value={fmt.block}
            aria-label="Paragraph style"
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => setBlock(e.target.value as BlockTag)}
          >
            {BLOCK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="nm-toolbar-sep" />
          <div className="nm-toolbar-group">
            <Tool label={`Bold (${MOD}+B)`} active={fmt.bold} onClick={() => exec("bold")} testId="tool-bold"><Bold size={16} aria-hidden="true" /></Tool>
            <Tool label={`Italic (${MOD}+I)`} active={fmt.italic} onClick={() => exec("italic")}><Italic size={16} aria-hidden="true" /></Tool>
            <Tool label={`Underline (${MOD}+U)`} active={fmt.underline} onClick={() => exec("underline")}><Underline size={16} aria-hidden="true" /></Tool>
            <Tool label={`Strikethrough (${MOD}+Shift+X)`} active={fmt.strike} onClick={() => exec("strikeThrough")}><Strikethrough size={16} aria-hidden="true" /></Tool>
          </div>
          <span className="nm-toolbar-sep" />
          {/* Text colour + cell fill. The swatches cancel mousedown so the
              body keeps its selection — applyInk/applyFill read it live. */}
          <div className="nm-toolbar-group nm-pop-wrap">
            <Tool
              label="Text and cell colour"
              active={!!fmt.ink || !!fmt.fill}
              testId="tool-ink"
              onClick={() => setOpenPop(openPop === "ink" ? null : "ink")}
            >
              <Baseline size={16} aria-hidden="true" />
            </Tool>
            {openPop === "ink" && (
              <div className="nm-pop is-left nm-ink-pop" role="dialog" aria-label="Text and cell colour">
                <p className="nm-ink-head"><Baseline size={13} aria-hidden="true" /> Text</p>
                <div className="nm-ink-row">
                  {NOTE_INK_KEYS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`nm-ink-dot${fmt.ink === key ? " is-active" : ""}`}
                      data-ink={key}
                      title={INK_LABELS[key]}
                      aria-label={`Text colour ${INK_LABELS[key]}`}
                      aria-pressed={fmt.ink === key}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyInk(key)}
                    >
                      A
                    </button>
                  ))}
                  <button
                    type="button"
                    className="nm-ink-none"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyInk(null)}
                  >
                    None
                  </button>
                </div>
                <div className="nm-menu-sep" />
                <p className="nm-ink-head"><PaintBucket size={13} aria-hidden="true" /> Cell fill</p>
                {fmt.table ? (
                  <div className="nm-ink-row">
                    {NOTE_INK_KEYS.map((key) => (
                      <button
                        key={key}
                        type="button"
                        className={`nm-fill-dot${fmt.fill === key ? " is-active" : ""}`}
                        data-ink={key}
                        title={INK_LABELS[key]}
                        aria-label={`Cell fill ${INK_LABELS[key]}`}
                        aria-pressed={fmt.fill === key}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyFill(key)}
                      >
                        <span aria-hidden="true" />
                      </button>
                    ))}
                    <button
                      type="button"
                      className="nm-ink-none"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyFill(null)}
                    >
                      None
                    </button>
                  </div>
                ) : (
                  <p className="nm-ink-hint">Put the caret in a table cell to fill it.</p>
                )}
              </div>
            )}
          </div>
          <span className="nm-toolbar-sep" />
          <div className="nm-toolbar-group">
            <Tool label="Bulleted list (type - and space)" active={fmt.ul} onClick={() => exec("insertUnorderedList")}><List size={16} aria-hidden="true" /></Tool>
            <Tool label="Numbered list (type 1. and space)" active={fmt.ol} onClick={() => exec("insertOrderedList")}><ListOrdered size={16} aria-hidden="true" /></Tool>
            <Tool label="Checklist (type [] and space)" active={fmt.checklist} onClick={toggleChecklist} testId="tool-checklist"><ListChecks size={16} aria-hidden="true" /></Tool>
          </div>
          <span className="nm-toolbar-sep" />
          <div className="nm-toolbar-group nm-pop-wrap">
            <Tool label={`Link (${MOD}+K)`} onClick={openLink}><Link2 size={16} aria-hidden="true" /></Tool>
            {openPop === "link" && (
              <div className="nm-pop is-left" role="dialog" aria-label="Add link">
                <form
                  className="nm-link-form"
                  onSubmit={(e) => { e.preventDefault(); applyLink(); }}
                >
                  <input
                    className="gt-input"
                    autoFocus
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://…  (empty removes the link)"
                    aria-label="Link URL"
                  />
                  <button type="submit" className="gt-btn is-primary is-sm">Apply</button>
                </form>
              </div>
            )}
            <Tool label="Divider" onClick={() => exec("insertHorizontalRule")}><Minus size={16} aria-hidden="true" /></Tool>
            <Tool
              label={fmt.table ? "Table options" : "Insert table"}
              active={fmt.table}
              testId="tool-table"
              onClick={() => {
                const sel = window.getSelection();
                savedRangeRef.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                refreshColumn();
                setOpenPop(openPop === "table" ? null : "table");
              }}
            >
              <Table2 size={16} aria-hidden="true" />
            </Tool>
            {openPop === "table" && (
              <div className="nm-pop is-left nm-table-pop" role="dialog" aria-label={fmt.table ? "Table options" : "Insert table"}>
                {fmt.table ? (
                  <>
                    {colState && (
                      <>
                        <p className="nm-ink-head" title={`Column ${colName(colState.col)}`}>
                          <Filter size={13} aria-hidden="true" /> {colState.label}
                        </p>
                        {menuItem(
                          colState.sort === "asc" ? "Sorted A → Z" : "Sort A → Z",
                          <ArrowUpAZ size={15} aria-hidden="true" />,
                          () => sortColumn("asc"),
                        )}
                        {menuItem(
                          colState.sort === "desc" ? "Sorted Z → A" : "Sort Z → A",
                          <ArrowDownAZ size={15} aria-hidden="true" />,
                          () => sortColumn("desc"),
                        )}
                        <form
                          className="nm-col-filter"
                          onSubmit={(e) => { e.preventDefault(); filterColumn(filterDraft, "contains"); }}
                        >
                          <input
                            className="gt-input"
                            value={filterDraft}
                            placeholder="Filter: contains…"
                            aria-label={`Filter rows where ${colState.label} contains`}
                            onChange={(e) => setFilterDraft(e.target.value)}
                          />
                          <button type="submit" className="gt-btn is-primary is-sm">Filter</button>
                        </form>
                        {/* The column's own values, so the common case is one
                            click and nothing to type. */}
                        {distinctColumnValues(colState.values).length > 1 && (
                          <div className="nm-col-values">
                            {distinctColumnValues(colState.values).map((value) => (
                              <button
                                key={value}
                                type="button"
                                className="nm-col-chip"
                                title={`Show only rows where ${colState.label} is ${value}`}
                                aria-label={`Show only rows where ${colState.label} is ${value}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => filterColumn(value, "equals")}
                              >
                                {value}
                              </button>
                            ))}
                          </div>
                        )}
                        {colState.hidden > 0
                          && menuItem(
                            `Clear filter (${colState.hidden} of ${colState.rows} hidden)`,
                            <FilterX size={15} aria-hidden="true" />,
                            () => clearFilter(popColumn()?.table || null),
                          )}
                        <div className="nm-menu-sep" />
                      </>
                    )}
                    {menuItem("Add row below", <Table2 size={15} aria-hidden="true" />, tableAddRow)}
                    {menuItem("Add column right", <Table2 size={15} aria-hidden="true" />, tableAddColumn)}
                    <div className="nm-menu-sep" />
                    {menuItem("Move column left", <ChevronLeft size={15} aria-hidden="true" />, () => tableMoveColumn(-1))}
                    {menuItem("Move column right", <ChevronRight size={15} aria-hidden="true" />, () => tableMoveColumn(1))}
                    {menuItem("Reset column widths", <Columns3 size={15} aria-hidden="true" />, tableResetWidths)}
                    <div className="nm-menu-sep" />
                    {menuItem("Delete row", <Minus size={15} aria-hidden="true" />, tableDeleteRow)}
                    {menuItem("Delete column", <Minus size={15} aria-hidden="true" />, tableDeleteColumn)}
                    {menuItem("Delete table", <Trash2 size={15} aria-hidden="true" />, tableDelete, { danger: true })}
                    <div className="nm-menu-sep" />
                    <p className="nm-formula-help">
                      Drag a header's right edge to resize a column, its left grip to move it.
                      Formulas follow the cells: move, add, delete or sort and the refs come
                      along. A <code>$</code> (as in <code>$A$1</code>) stops a ref shifting
                      when the formula is COPIED somewhere else.
                    </p>
                    <div className="nm-menu-sep" />
                    <p className="nm-formula-help">
                      Formulas: start a cell with <code>=</code>. Cells are A1, B2… (header row is row 1).
                      <br />
                      {FORMULA_FUNCTIONS.join(", ")}
                    </p>
                  </>
                ) : (
                  <form
                    className="nm-table-form"
                    // Our own clamp decides the size; without this the browser
                    // refuses to submit while a box is empty or over max.
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      const rows = clampTableSize(tableSize.rows);
                      const cols = clampTableSize(tableSize.cols);
                      // Write the clamped pair back, so an emptied box shows
                      // what was actually inserted next time it opens.
                      setTableSize({ rows: String(rows), cols: String(cols) });
                      insertTable(rows, cols);
                    }}
                  >
                    <label>
                      Rows
                      <input
                        className="gt-input"
                        type="number"
                        min={1}
                        max={TABLE_MAX}
                        value={tableSize.rows}
                        autoFocus
                        onChange={(e) => setTableSize((t) => ({ ...t, rows: e.target.value.replace(/\D/g, "").slice(0, 2) }))}
                      />
                    </label>
                    <label>
                      Columns
                      <input
                        className="gt-input"
                        type="number"
                        min={1}
                        max={TABLE_MAX}
                        value={tableSize.cols}
                        onChange={(e) => setTableSize((t) => ({ ...t, cols: e.target.value.replace(/\D/g, "").slice(0, 2) }))}
                      />
                    </label>
                    <button type="submit" className="gt-btn is-primary is-sm">Insert</button>
                  </form>
                )}
              </div>
            )}
            <Tool label="Clear formatting" onClick={() => { exec("removeFormat"); exec("formatBlock", "<p>"); }}><RemoveFormatting size={16} aria-hidden="true" /></Tool>
          </div>
        </div>
      )}

      {!readOnly && fmt.table && cellInfo && (
        <div className="nm-formula-bar" data-testid="formula-bar">
          <span className="nm-cell-addr" aria-label="Cell">{cellInfo.addr}</span>
          <span className="nm-fx" aria-hidden="true">fx</span>
          {cellInfo.formula ? (
            <code className="nm-formula-text">{cellInfo.formula}</code>
          ) : (
            <span className="nm-formula-hint">
              Type <kbd>=</kbd> for a formula, e.g. <code>=SUM(B2:B5)</code>, <code>=A2*B2</code>, <code>=IF(C2&gt;100,"High","Low")</code>
            </span>
          )}
        </div>
      )}

      {/* An active filter has to be visible on its own: a hidden row is
          display:none, and a read-only viewer has neither toolbar nor caret. */}
      {hiddenRows > 0 && (
        <div className="nm-filter-bar" data-testid="filter-bar">
          <Filter size={13} aria-hidden="true" />
          <span>
            {hiddenRows} {hiddenRows === 1 ? "row is" : "rows are"} hidden by a column filter
          </span>
          {!readOnly && (
            <button
              type="button"
              className="nm-filter-clear"
              onClick={() => clearFilter(bodyRef.current)}
            >
              Show all rows
            </button>
          )}
        </div>
      )}

      <div className="nm-body-scroll">
        {!readOnly && bodyEmpty && !title && (
          <div className="nm-hint" aria-hidden="true">
            Type <kbd>-</kbd> <kbd>space</kbd> for a list, <kbd>[]</kbd> <kbd>space</kbd> for a checklist, <kbd>#</kbd> <kbd>space</kbd> for a heading. Everything saves automatically.
          </div>
        )}
        <div
          ref={bodyRef}
          className={`nm-content${bodyEmpty ? " is-empty" : ""}`}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Note body"
          aria-readonly={readOnly}
          data-placeholder={readOnly ? "" : "Write your note…"}
          spellCheck
          onInput={emitBody}
          onPaste={handlePaste}
          onCopy={(e) => copyCellRange(e, false)}
          onCut={(e) => copyCellRange(e, true)}
          onKeyDown={handleKeyDown}
          onClick={handleBodyClick}
          onPointerDown={onBodyPointerDown}
          onPointerMove={onBodyPointerMove}
          onPointerUp={onBodyPointerUp}
          onPointerCancel={onBodyPointerUp}
          onFocus={refreshFormat}
        />
      </div>

      <footer className="nm-editor-foot">
        <div className="nm-tags" aria-label="Tags">
          {note.tags.map((tag) => (
            <span key={tag} className="nm-tag">
              #{tag}
              {ownerControls && (
                <button type="button" onClick={() => removeTag(tag)} aria-label={`Remove tag ${tag}`}>
                  <X size={11} aria-hidden="true" />
                </button>
              )}
            </span>
          ))}
          {ownerControls && (
            <>
              <input
                className="nm-tag-input"
                value={tagDraft}
                list={tagListId}
                placeholder={note.tags.length ? "+ tag" : "+ add tag"}
                aria-label="Add a tag"
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={onTagKey}
                onBlur={() => tagDraft && commitTag(tagDraft)}
              />
              <datalist id={tagListId}>
                {tagSuggestions.filter((t) => !note.tags.includes(t)).map((t) => <option key={t} value={t} />)}
              </datalist>
            </>
          )}
        </div>
        <div className="nm-foot-meta">
          <span>{words} {words === 1 ? "word" : "words"}</span>
          {note.created_at && <span title={fullTimestamp(note.created_at)}>Created {relativeTime(note.created_at)}</span>}
        </div>
      </footer>
    </section>
  );
}
